import type { Event } from "@/lib/event-schema";

export const imamAli2026: Event = {
  slug: "imam-ali-2026",
  title: "Imam Ali Celebration 2026",
  tagline: "An afternoon of remembrance, food, and gathering.",
  status: "published",
  modulesOrder: [
    "hero",
    "agenda",
    "location",
    "rsvp",
    "potluck",
    "competition",
    "junior",
    "donation",
  ],
  createdAt: Date.parse("2026-04-01"),
  updatedAt: Date.now(),
  modules: {
    hero: {
      enabled: true,
      eyebrow: "13 Rajab · A community gathering",
      title: "A festival in honor of *Imam Ali* (AS)",
      subtitle:
        "Join families from across San Diego for a gentle afternoon of prayer, food, poetry, and play.",
      date: "Saturday, January 3rd · 2026",
      location: "Mobeen Center · Main Hall",
      banner: {
        src: "/assets/images/imam-ali-2026-banner.jpeg",
        alt: "Imam Ali Celebration 2026 — Mobeen Center",
      },
      ctas: [
        { label: "Reserve a seat", to: "/imam-ali-2026/rsvp" },
        { label: "Sign up for potluck", to: "/imam-ali-2026/potluck", variant: "outline" },
        { label: "Cake competition", to: "/imam-ali-2026/competition", variant: "subtle" },
      ],
    },
    agenda: {
      enabled: true,
      navLabel: "Schedule",
      title: "The day, hour by hour",
      dateLabel: "Saturday, January 3rd · 2026",
      items: [
        { start: "12:15 PM", end: "1:00 PM", title: "Opening & prayer", icon: "prayer" },
        { start: "1:00 PM", end: "2:00 PM", title: "Potluck lunch", icon: "meal" },
        {
          start: "2:00 PM",
          end: "2:30 PM",
          title: "Quran & Ziyarat Amin Allah",
          icon: "prayer",
        },
        {
          start: "2:30 PM",
          end: "3:15 PM",
          title: "Keynote — Seyed Ali Musawi",
          description: "Reflections on the legacy of Imam Ali (AS).",
          icon: "speech",
        },
        {
          start: "3:20 PM",
          end: "3:40 PM",
          title: "Poem recitation — Mehdi Ghavami",
          icon: "poem",
        },
        {
          start: "3:40 PM",
          end: "4:00 PM",
          title: "FunPetition with dads and little ones",
          icon: "game",
        },
        {
          start: "4:00 PM",
          end: "4:30 PM",
          title: "Cake competition judging",
          icon: "award",
        },
        {
          start: "2:15 PM",
          end: "4:15 PM",
          title: "Kids game stations",
          description: "Open play, crafts, and stories — running in parallel.",
          icon: "kids",
          highlight: true,
        },
      ],
    },
    location: {
      enabled: true,
      navLabel: "Where",
      title: "Find us at the Mobeen Center",
      address: "Mobeen Center\n9625 Black Mountain Rd\nSan Diego, CA 92126",
      lat: 32.89775935422561,
      lng: -117.12279309723516,
      zoom: 15,
      showDirections: true,
    },
    rsvp: {
      enabled: true,
      navLabel: "RSVP",
      title: "RSVP for your family",
      description:
        "We pace the food and seating to who's coming. Tell us how many — it takes a minute.",
      ctaLabel: "Confirm RSVP",
      capacity: 220,
    },
    potluck: {
      enabled: true,
      navLabel: "Potluck",
      title: "Bring a dish to share",
      description:
        "Every family contributes one dish. Pick a category so we don't all bring rice.",
      notice:
        "All food must be halal. No pork, no alcohol, no shared utensils with non-halal cooking. Please use clean cutting boards. When in doubt, ask.",
      showSubmissions: true,
      categories: [
        { id: "main", label: "Main Dish", emoji: "🍚" },
        { id: "appetizer", label: "Appetizer/Side", emoji: "🥗" },
        { id: "dessert", label: "Dessert", emoji: "🍮" },
        { id: "drink", label: "Drink/Beverage", emoji: "🍵" },
        { id: "nuts", label: "Nuts and Sweets", emoji: "🍬" },
      ],
    },
    competition: {
      enabled: true,
      navLabel: "Cake contest",
      title: "Bake-off for Imam Ali (AS)",
      description:
        "Bring your most thoughtful cake. The room tastes, ranks, and crowns a winner. Halal ingredients only, please.",
      entryNoun: "cake",
      participantNoun: "baker",
      kind: "Cake competition",
      judgingMode: "ranking",
      showEntries: true,
      fields: [
        {
          id: "bakerName",
          label: "Your name",
          type: "text",
          required: true,
          placeholder: "Layla",
          publiclyVisible: true,
        },
        {
          id: "cakeTitle",
          label: "Cake name",
          type: "text",
          required: true,
          placeholder: "Saffron rose cardamom",
          publiclyVisible: true,
        },
        {
          id: "description",
          label: "A line or two about it",
          type: "longtext",
          placeholder: "What makes it yours?",
          publiclyVisible: true,
        },
        {
          id: "contactInfo",
          label: "Phone or email",
          type: "text",
          required: true,
          hint: "So we can reach you on the day. Kept private.",
          publiclyVisible: false,
        },
        {
          id: "isVegan",
          label: "Vegan",
          type: "boolean",
          hint: "No animal products.",
          publiclyVisible: true,
        },
        {
          id: "containsNuts",
          label: "Contains nuts",
          type: "boolean",
          hint: "Helps families with allergies.",
          publiclyVisible: true,
        },
      ],
    },
    junior: {
      enabled: true,
      navLabel: "Juniors",
      title: "While the grown-ups gather",
      description:
        "A parallel program for younger ones, supervised, gentle, with snacks.",
      ageGroups: [
        { label: "3 – 5", description: "Sensory play, story circle, simple crafts." },
        { label: "6 – 9", description: "Calligraphy, group games, a short workshop." },
        { label: "10 – 13", description: "Discussion circle, board games, a guest speaker." },
      ],
      stations: [
        { name: "Calligraphy corner", description: "Practice the Bismillah on parchment." },
        { name: "Story circle", description: "Two stories about generosity and courage." },
        { name: "Quiet zone", description: "Books and cushions for a breather." },
        { name: "Open craft", description: "Make a lantern to take home." },
      ],
    },
    donation: {
      enabled: true,
      navLabel: "Donate",
      title: "Keep the Mobeen Center running",
      description:
        "Your contribution goes toward the center's upkeep — rent, utilities, and the routine costs of keeping our doors open year-round. Food for events is covered separately by sponsors.",
      suggestedAmounts: [20, 50, 100, 250],
      allowCustom: true,
      goalCents: 500000,
      raisedCents: 134250,
      stripeReady: false,
    },
  },
};

export const seededEvents: Record<string, Event> = {
  [imamAli2026.slug]: imamAli2026,
};
